/**
 * Mobile-only track selector: one full-width bar at the bottom.
 * Expanded = track pills (full width) + collapse chevron. Collapsed = thin strip with chevron to expand.
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useTrack } from '../contexts/TrackContext';
import { useIsMobile } from '../hooks/useIsMobile';

const ALL_TRACK_ITEMS = [
  { value: 'all', label: 'All' },
  { value: 'mck', label: 'McKinsey' },
  { value: 'bcg', label: 'BCG' },
];

const TWO_TRACK_ITEMS = [
  { value: 'mck', label: 'McKinsey' },
  { value: 'bcg', label: 'BCG' },
];

const cx = (...classes) => classes.filter(Boolean).join(' ');

export default function FloatingTrackSelector() {
  const { pathname } = useLocation();
  const { track, setTrack } = useTrack();
  const isMobile = useIsMobile(767);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const onLanding = pathname === '/';
  const showOn = onLanding || pathname === '/try-for-free';
  if (!isMobile || !showOn) return null;

  const items = onLanding ? ALL_TRACK_ITEMS : TWO_TRACK_ITEMS;

  return (
    <motion.div
      className={cx(
        'fixed bottom-0 left-0 right-0 z-40 flex items-center gap-2 border-t border-slate-200/80 bg-white/75 backdrop-blur-md px-4 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]',
        isCollapsed ? 'justify-center py-2' : 'py-3'
      )}
      aria-label="Track selector"
      initial={false}
      animate={{
        minHeight: isCollapsed ? 40 : 72,
      }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
    >
      {/* Track pills: full width when expanded; no space when collapsed */}
      <motion.div
        className={cx(
          'flex min-w-0 overflow-hidden rounded-xl border border-slate-200/50 bg-slate-50/50 p-1',
          isCollapsed ? 'w-0 flex-none opacity-0' : 'flex-1'
        )}
        initial={false}
        animate={{ opacity: isCollapsed ? 0 : 1 }}
        transition={{ type: 'spring', stiffness: 500, damping: 35 }}
      >
        <div className="relative flex flex-1 min-w-0">
          <motion.div
            className="absolute inset-y-0 rounded-lg bg-gradient-to-r from-blue-600 to-blue-400 shadow-md"
            style={{ width: `${100 / items.length}%` }}
            animate={{ left: `${Math.max(0, items.findIndex((it) => it.value === track)) * (100 / items.length)}%` }}
            transition={{ type: 'spring', stiffness: 400, damping: 28 }}
          />
          {items.map((it) => (
            <button
              key={it.value}
              type="button"
              onClick={() => setTrack(it.value)}
              className={`relative flex-1 py-2 text-sm font-medium rounded-lg transition-colors duration-150 ${
                track === it.value ? 'text-white' : 'text-slate-600 hover:bg-white/70 hover:text-slate-800'
              }`}
            >
              <span className="relative z-10 block">{it.label}</span>
            </button>
          ))}
        </div>
      </motion.div>

      {/* One chevron: collapse when expanded, expand when collapsed */}
      <button
        type="button"
        onClick={() => setIsCollapsed((c) => !c)}
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"
        aria-label={isCollapsed ? 'Expand track selector' : 'Collapse track selector'}
      >
        <motion.svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          initial={false}
          animate={{ rotate: isCollapsed ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </motion.svg>
      </button>
    </motion.div>
  );
}
